import { useCallback, useEffect, useState } from 'react';
import Parse, { SUPER_ADMIN_EMAIL } from '../api/config';
import { useAuth } from '../components/AuthContext';

// Whether the signed-in person may administer people. The server decides;
// this only tells the UI what to draw.
export const usePermissions = () => {
    const { user, isImpersonating } = useAuth();
    const [permissions, setPermissions] = useState({
        isAdmin: false,
        isSuperAdmin: false,
        hasSession: false,
        serverEmail: '',
        roles: [],
    });
    const [loading, setLoading] = useState(true);

    const empty = { isAdmin: false, isSuperAdmin: false, hasSession: false, serverEmail: '', roles: [] };

    const refresh = useCallback(async () => {
        if (!user?.email) {
            setPermissions(empty);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            if (isImpersonating) {
                // Show the impersonated person's own permissions, so the nav and
                // gated areas reflect exactly what they'd see. getMyAdminStatus
                // would report the real admin, so use the target's roles instead.
                const { roles = [] } = await Parse.Cloud.run('getUserRoles', { email: user.email });
                const targetIsSuper = (user.email || '').toLowerCase() === SUPER_ADMIN_EMAIL;
                setPermissions({
                    isAdmin: roles.includes('Administrator') || targetIsSuper,
                    isSuperAdmin: targetIsSuper,
                    hasSession: true,
                    serverEmail: user.email || '',
                    roles,
                });
            } else {
                const result = await Parse.Cloud.run('getMyAdminStatus');
                setPermissions({
                    isAdmin: !!result.isAdmin,
                    isSuperAdmin: !!result.isSuperAdmin,
                    hasSession: !!result.hasSession,
                    serverEmail: result.email || '',
                    roles: result.roles || [],
                });
            }
        } catch (error) {
            console.warn('Could not check your permissions:', error);
            setPermissions(empty);
        } finally {
            setLoading(false);
        }
    }, [user, isImpersonating]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Access to a role-scoped area: an admin sees everything; otherwise you
    // need the specific role.
    const canAccess = (role) => permissions.isAdmin || permissions.roles.includes(role);

    return { ...permissions, loading, refresh, canAccess };
};
